import { type Graph, nodeById } from '../models/graph';
import { edmondsKarpResidual } from './maxFlow';

export function findMinCut(graph: Graph, sourceId: string, sinkId: string): string[] {
  if (sourceId === sinkId) return [];
  if (!nodeById(graph, sourceId) || !nodeById(graph, sinkId)) return [];

  const residual = edmondsKarpResidual(graph, sourceId, sinkId);

  const reachable = new Set<string>([sourceId]);
  const queue: string[] = [sourceId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const edge of residual.get(current) ?? []) {
      if (!reachable.has(edge.to) && edge.capacity > 0) {
        reachable.add(edge.to);
        queue.push(edge.to);
      }
    }
  }

  const cutEdgeIds: string[] = [];
  for (const edge of graph.edges) {
    const sourceSide = reachable.has(edge.sourceId);
    const targetSide = reachable.has(edge.targetId);
    if (graph.directed) {
      if (sourceSide && !targetSide) cutEdgeIds.push(edge.id);
    } else if (sourceSide !== targetSide) {
      cutEdgeIds.push(edge.id);
    }
  }

  return cutEdgeIds;
}

export function minCutCapacity(graph: Graph, sourceId: string, sinkId: string): number {
  return findMinCut(graph, sourceId, sinkId).reduce(
    (sum, id) => sum + (graph.edges.find((e) => e.id === id)?.capacity ?? 0),
    0,
  );
}
